import { ColorEnum } from '@/constants/color'
import Cutout from '@/features/common/components/Cutout'
import AccountBadge from '@/features/openTabs/components/AccountBadge'
import PenIcon from '@/features/openTabs/icons/PenIcon'
import IconButton from '@/features/ui/buttons/components/IconButton'
import CloseIcon from '@/features/ui/icons/components/CloseIcon'
import Typography from '@/features/ui/typography'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface AccountHeaderProps {
    accountNumber: number
    accountName: string
    closed?: boolean
    onRename?: () => void
    onClose: () => void
}

const AccountHeader: FC<AccountHeaderProps> = ({
    accountNumber,
    accountName,
    closed = false,
    onRename,
    onClose,
}) => {
    const { t } = useTranslation('app')
    const { styles } = useStyles(stylesheet)

    return (
        <View style={styles.wrapper}>
            <View style={styles.container}>
                <View style={styles.accountInfo}>
                    <AccountBadge number={accountNumber} />
                    <View style={styles.text}>
                        <Typography.Heading variant="h3" numberOfLines={1}>
                            {accountName}
                        </Typography.Heading>
                        <Typography.Description size="tiny" style={styles.subtitle}>
                            {t(`openTabs.account.${closed ? 'closed' : 'opened'}`)}
                        </Typography.Description>
                    </View>
                </View>

                <View style={styles.actions}>
                    {!closed && (
                        <IconButton
                            accessibilityLabel={t('openTabs.actions.rename')}
                            accessibilityRole="button"
                            Icon={PenIcon}
                            color="secondary"
                            type="filled"
                            disabled={!onRename}
                            onPress={onRename}
                            style={styles.iconButton}
                        />
                    )}
                    <IconButton
                        accessibilityLabel={t('openTabs.actions.closeDetail')}
                        accessibilityRole="button"
                        Icon={CloseIcon}
                        size={24}
                        iconSize={24}
                        onPress={onClose}
                        style={styles.closeButton}
                    />
                </View>
            </View>
            <Cutout />
        </View>
    )
}

const stylesheet = createStyleSheet({
    wrapper: {
        width: '100%',
        backgroundColor: ColorEnum.WHITE,
    },
    container: {
        height: 72,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 16,
        backgroundColor: ColorEnum.GRAY_100,
    },
    accountInfo: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    text: {
        flex: 1,
        minWidth: 0,
    },
    subtitle: {
        color: ColorEnum.JET_100,
    },
    actions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginLeft: 16,
    },
    iconButton: {
        borderRadius: 8,
    },
    closeButton: {
        marginLeft: 8,
    },
})

export default AccountHeader
